import React, { useState, useEffect } from 'react';
import { Printer, Download, X, FileText, Receipt, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

// Helper to convert number to words (Indian Rupees format)
const numberToWords = (num) => {
  if (!num || isNaN(num)) return 'Zero Rupees Only';
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const inWords = (n) => {
    if ((n = n.toString()).length > 9) return 'Overflow';
    const n_array = ('000000000' + n).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n_array) return '';
    let str = '';
    str += (Number(n_array[1]) !== 0) ? (a[Number(n_array[1])] || b[n_array[1][0]] + ' ' + a[n_array[1][1]]) + 'Crore ' : '';
    str += (Number(n_array[2]) !== 0) ? (a[Number(n_array[2])] || b[n_array[2][0]] + ' ' + a[n_array[2][1]]) + 'Lakh ' : '';
    str += (Number(n_array[3]) !== 0) ? (a[Number(n_array[3])] || b[n_array[3][0]] + ' ' + a[n_array[3][1]]) + 'Thousand ' : '';
    str += (Number(n_array[4]) !== 0) ? (a[Number(n_array[4])] || b[n_array[4][0]] + ' ' + a[n_array[4][1]]) + 'Hundred ' : '';
    str += (Number(n_array[5]) !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n_array[5])] || b[n_array[5][0]] + ' ' + a[n_array[5][1]]) : '';
    return str;
  };

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);
  let words = inWords(integerPart) + 'Rupees';
  if (decimalPart > 0) {
    words += ' and ' + inWords(decimalPart) + 'Paise';
  }
  return words + ' Only';
};

const PurchasePrintModal = ({ isOpen, onClose, billData, initialFormat = 'A4' }) => {
  const [printFormat, setPrintFormat] = useState(initialFormat);

  useEffect(() => {
    if (initialFormat) {
      setPrintFormat(initialFormat);
    }
  }, [initialFormat, isOpen]);

  if (!isOpen || !billData) return null;

  const {
    invoiceNumber,
    transactionDate,
    supplier,
    items = [],
    paymentMode = 'CASH',
    notes = '',
    branch,
    companySettings,
    currencySymbol = '₹'
  } = billData;

  const formattedDate = transactionDate
    ? new Date(transactionDate).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : new Date().toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });

  // Calculate totals
  const subtotal = items.reduce((acc, item) => acc + (parseFloat(item.amount) || (parseFloat(item.quantity) * parseFloat(item.rate)) || 0), 0);
  const totalGst = items.reduce((acc, item) => acc + (parseFloat(item.gstAmount) || 0), 0);
  const grandTotal = items.reduce((acc, item) => acc + (parseFloat(item.totalAmount) || (parseFloat(item.amount || 0) + parseFloat(item.gstAmount || 0)) || 0), 0);

  const shopName = companySettings?.companyName || branch?.name || 'Digital Repair Shop';
  const shopAddress = branch?.address || companySettings?.address || '';
  const shopPhone = branch?.phone || companySettings?.phone || '';
  const shopEmail = branch?.email || companySettings?.email || '';
  const shopGst = companySettings?.gstNumber || '';

  const handleTriggerPrint = () => {
    setTimeout(() => {
      window.print();
    }, 150);
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('purchase-print-area');
    if (!element) return;

    const runDownload = () => {
      toast.loading('Generating PDF...', { id: 'pdf-toast' });
      const opt = {
        margin: printFormat === 'THERMAL' ? 2 : 8,
        filename: `Purchase_Bill_${invoiceNumber || 'INV'}_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, logging: false },
        jsPDF: printFormat === 'THERMAL' 
          ? { unit: 'mm', format: [80, 297], orientation: 'portrait' } 
          : { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      window.html2pdf().from(element).set(opt).save()
        .then(() => {
          toast.dismiss('pdf-toast');
          toast.success('PDF downloaded successfully!');
        })
        .catch((err) => {
          console.error('PDF error:', err);
          toast.dismiss('pdf-toast');
          toast.error('Failed to generate PDF. Opening print dialog...');
          window.print();
        });
    };

    if (!window.html2pdf) {
      toast.loading('Loading PDF library...', { id: 'pdf-toast' });
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = () => {
        toast.dismiss('pdf-toast');
        runDownload();
      };
      script.onerror = () => {
        toast.dismiss('pdf-toast');
        toast.error('Failed to load PDF library');
      };
      document.head.appendChild(script);
    } else {
      runDownload();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
      {/* Dynamic CSS Print Media Rule */}
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          #purchase-print-modal-container, #purchase-print-modal-container * {
            visibility: visible !important;
          }
          #purchase-print-modal-container {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            box-shadow: none !important;
          }
          .no-print-action {
            display: none !important;
          }
          @page {
            size: ${printFormat === 'THERMAL' ? '80mm auto' : 'A4 portrait'};
            margin: ${printFormat === 'THERMAL' ? '2mm' : '8mm 10mm'};
          }
        }
      `}</style>

      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-200">
        {/* Modal Top Header Bar */}
        <div className="no-print-action bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-600/30 rounded-lg text-blue-400 border border-blue-500/30">
              <Receipt className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white tracking-wide flex items-center gap-2">
                Purchase Invoice / Stock Bill
                <span className="text-xs font-semibold px-2 py-0.5 bg-green-500/20 text-green-300 border border-green-500/30 rounded-full">
                  Saved
                </span>
              </h2>
              <p className="text-xs text-slate-400">Invoice #{invoiceNumber || 'N/A'} • {formattedDate}</p>
            </div>
          </div>

          {/* Format Switcher & Action Buttons */}
          <div className="flex items-center space-x-3">
            <div className="bg-slate-800 p-1 rounded-lg border border-slate-700 flex items-center">
              <button
                type="button"
                onClick={() => setPrintFormat('A4')}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center ${
                  printFormat === 'A4'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                A4 Sheet
              </button>
              <button
                type="button"
                onClick={() => setPrintFormat('THERMAL')}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center ${
                  printFormat === 'THERMAL'
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Receipt className="w-3.5 h-3.5 mr-1.5" />
                Thermal (80mm)
              </button>
            </div>

            <button
              onClick={handleTriggerPrint}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-xs font-bold transition-colors inline-flex items-center shadow-md hover:shadow-lg"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              Print Bill
            </button>

            <button
              onClick={handleDownloadPDF}
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3 py-2 rounded-lg text-xs font-semibold transition-colors inline-flex items-center"
            >
              <Download className="w-4 h-4 mr-1.5" />
              PDF
            </button>

            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-slate-800 p-2 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Area Scroll View */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-6 flex justify-center" id="purchase-print-modal-container">
          {printFormat === 'A4' ? (
            /* ========================================================================= */
            /* A4 SHEET FORMAT PRINT LAYOUT                                              */
            /* ========================================================================= */
            <div
              id="purchase-print-area"
              className="bg-white text-slate-900 shadow-xl rounded-lg p-8 w-full max-w-[210mm] min-h-[297mm] font-sans border border-gray-200 transition-all"
            >
              {/* Header Section */}
              <div className="flex justify-between items-start border-b-2 border-slate-900 pb-6 mb-6">
                <div>
                  {companySettings?.logo ? (
                    <img
                      src={companySettings.logo}
                      alt="Logo"
                      className="h-16 w-auto max-w-[200px] object-contain mb-2"
                    />
                  ) : null}
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">{shopName}</h1>
                  {shopAddress && <p className="text-xs text-slate-600 mt-1 max-w-sm">{shopAddress}</p>}
                  <div className="text-xs text-slate-600 mt-1 space-x-3">
                    {shopPhone && <span><strong>Ph:</strong> {shopPhone}</span>}
                    {shopEmail && <span><strong>Email:</strong> {shopEmail}</span>}
                  </div>
                  {shopGst && <p className="text-xs font-bold text-blue-700 mt-1">GSTIN: {shopGst}</p>}
                </div>

                <div className="text-right">
                  <div className="inline-block bg-slate-900 text-white px-4 py-1.5 rounded text-sm font-black tracking-widest uppercase mb-3">
                    PURCHASE INVOICE
                  </div>
                  <table className="text-xs text-right ml-auto border-collapse">
                    <tbody>
                      <tr>
                        <td className="text-slate-500 font-semibold pr-3 py-0.5">Invoice No:</td>
                        <td className="font-bold text-slate-900 py-0.5">{invoiceNumber || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="text-slate-500 font-semibold pr-3 py-0.5">Date:</td>
                        <td className="font-bold text-slate-900 py-0.5">{formattedDate}</td>
                      </tr>
                      <tr>
                        <td className="text-slate-500 font-semibold pr-3 py-0.5">Payment Mode:</td>
                        <td className="font-bold text-emerald-700 py-0.5 uppercase">{paymentMode}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Supplier Information Card */}
              <div className="grid grid-cols-2 gap-6 bg-slate-50 rounded-lg p-4 border border-slate-200 mb-6">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Supplier Details</h3>
                  {supplier ? (
                    <div>
                      <p className="text-sm font-black text-slate-900">{supplier.name}</p>
                      {supplier.contactPerson && <p className="text-xs text-slate-600 mt-0.5">Attn: {supplier.contactPerson}</p>}
                      {supplier.address && <p className="text-xs text-slate-600 mt-0.5">{supplier.address}</p>}
                      {supplier.phone && <p className="text-xs text-slate-600 mt-0.5">Ph: {supplier.phone}</p>}
                      {supplier.gstNumber && <p className="text-xs font-bold text-slate-800 mt-1">GSTIN: {supplier.gstNumber}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">Cash Purchase / Counter Supplier</p>
                  )}
                </div>

                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Stock Branch / Receiving Location</h3>
                  <p className="text-sm font-black text-slate-900">{branch?.name || shopName}</p>
                  {branch?.code && <p className="text-xs text-slate-600 mt-0.5">Branch Code: {branch.code}</p>}
                  {branch?.address && <p className="text-xs text-slate-600 mt-0.5">{branch.address}</p>}
                </div>
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-900 text-white">
                      <th className="py-2.5 px-3 text-center w-10 font-bold border border-slate-800">#</th>
                      <th className="py-2.5 px-3 text-left font-bold border border-slate-800">Item Description</th>
                      <th className="py-2.5 px-3 text-left font-bold border border-slate-800">Brand / Model</th>
                      <th className="py-2.5 px-3 text-center font-bold border border-slate-800">HSN / Part No</th>
                      <th className="py-2.5 px-3 text-center font-bold border border-slate-800">Qty</th>
                      <th className="py-2.5 px-3 text-right font-bold border border-slate-800">Rate ({currencySymbol})</th>
                      <th className="py-2.5 px-3 text-right font-bold border border-slate-800">GST ({currencySymbol})</th>
                      <th className="py-2.5 px-3 text-right font-bold border border-slate-800">Total ({currencySymbol})</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 border-b border-slate-300">
                    {items.map((item, idx) => {
                      const prodName = item.product?.name || item.productName || 'Inventory Item';
                      const brandModel = `${item.product?.brand || ''} ${item.product?.model || ''}`.trim() || '-';
                      const hsnPart = item.product?.hsnCode || item.product?.partNumber || '-';
                      const qty = item.quantity || 0;
                      const rate = parseFloat(item.rate || 0);
                      const gst = parseFloat(item.gstAmount || 0);
                      const itemTotal = parseFloat(item.totalAmount || (qty * rate + gst));

                      return (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                          <td className="py-2.5 px-3 text-center font-semibold text-slate-500 border-x border-slate-200">{idx + 1}</td>
                          <td className="py-2.5 px-3 font-bold text-slate-900 border-x border-slate-200">{prodName}</td>
                          <td className="py-2.5 px-3 text-slate-700 border-x border-slate-200">{brandModel}</td>
                          <td className="py-2.5 px-3 text-center text-slate-600 border-x border-slate-200">{hsnPart}</td>
                          <td className="py-2.5 px-3 text-center font-bold text-slate-900 border-x border-slate-200">{qty} {item.product?.unit || 'PCS'}</td>
                          <td className="py-2.5 px-3 text-right text-slate-800 border-x border-slate-200">{currencySymbol}{rate.toFixed(2)}</td>
                          <td className="py-2.5 px-3 text-right text-slate-700 border-x border-slate-200">{currencySymbol}{gst.toFixed(2)}</td>
                          <td className="py-2.5 px-3 text-right font-bold text-slate-900 border-x border-slate-200">{currencySymbol}{itemTotal.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals & Summary */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 mb-3">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Amount in Words:</p>
                    <p className="text-xs font-bold text-slate-900 italic">{numberToWords(grandTotal)}</p>
                  </div>
                  {notes && (
                    <div className="bg-amber-50/60 rounded-lg p-3 border border-amber-200 text-xs">
                      <p className="font-bold text-amber-900 mb-0.5">Purchase Notes / Remarks:</p>
                      <p className="text-amber-800">{notes}</p>
                    </div>
                  )}
                </div>

                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 space-y-2 text-xs">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal:</span>
                    <span className="font-semibold text-slate-900">{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-600">
                    <span>Total GST Amount:</span>
                    <span className="font-semibold text-slate-900">{currencySymbol}{totalGst.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-slate-300 pt-2 flex justify-between text-sm font-black text-slate-900">
                    <span>Grand Total:</span>
                    <span className="text-blue-700 text-base">{currencySymbol}{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Signature Footer */}
              <div className="pt-12 mt-auto border-t border-slate-200 flex justify-between items-end text-xs">
                <div className="text-slate-500">
                  <p className="font-bold text-slate-700">Thank you for your business!</p>
                  <p className="text-[10px] mt-0.5">Computer generated purchase invoice.</p>
                </div>
                <div className="text-center">
                  <div className="w-44 border-b border-slate-400 mb-1"></div>
                  <p className="font-bold text-slate-900">Authorized Signature</p>
                  <p className="text-[10px] text-slate-500">{shopName}</p>
                </div>
              </div>
            </div>
          ) : (
            /* ========================================================================= */
            /* THERMAL PRINTER (80MM RECEIPT) FORMAT PRINT LAYOUT                         */
            /* ========================================================================= */
            <div
              id="purchase-print-area"
              className="bg-white text-slate-900 shadow-xl p-4 w-full max-w-[80mm] font-mono text-xs border border-gray-300 rounded-md"
            >
              {/* Header */}
              <div className="text-center pb-2 border-b border-dashed border-slate-800 mb-2">
                <h2 className="text-sm font-bold uppercase tracking-wider">{shopName}</h2>
                {shopAddress && <p className="text-[10px] leading-tight text-slate-700 mt-0.5">{shopAddress}</p>}
                {shopPhone && <p className="text-[10px]">Ph: {shopPhone}</p>}
                {shopGst && <p className="text-[10px] font-bold mt-0.5">GST: {shopGst}</p>}
                <p className="text-xs font-bold uppercase mt-2 border-t border-b border-slate-900 py-0.5">PURCHASE BILL</p>
              </div>

              {/* Details */}
              <div className="text-[10px] space-y-0.5 mb-2 pb-2 border-b border-dashed border-slate-800">
                <div className="flex justify-between">
                  <span>Invoice #:</span>
                  <span className="font-bold">{invoiceNumber || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Date:</span>
                  <span>{formattedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span>Mode:</span>
                  <span className="font-bold uppercase">{paymentMode}</span>
                </div>
                {supplier && (
                  <div className="flex justify-between text-slate-800">
                    <span>Supplier:</span>
                    <span className="font-bold truncate max-w-[120px]">{supplier.name}</span>
                  </div>
                )}
              </div>

              {/* Items List */}
              <div className="mb-2 pb-2 border-b border-dashed border-slate-800">
                <div className="flex justify-between text-[10px] font-bold border-b border-slate-400 pb-1 mb-1">
                  <span>ITEM</span>
                  <span>QTY x RATE</span>
                  <span>AMT</span>
                </div>
                {items.map((item, idx) => {
                  const prodName = item.product?.name || item.productName || 'Item';
                  const qty = item.quantity || 0;
                  const rate = parseFloat(item.rate || 0);
                  const gst = parseFloat(item.gstAmount || 0);
                  const total = parseFloat(item.totalAmount || (qty * rate + gst));

                  return (
                    <div key={idx} className="mb-1 text-[10px]">
                      <div className="font-bold truncate">{prodName}</div>
                      <div className="flex justify-between text-slate-600">
                        <span>{qty} {item.product?.unit || 'PCS'} @ {currencySymbol}{rate}</span>
                        <span className="font-bold text-slate-900">{currencySymbol}{total.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Totals */}
              <div className="text-[10px] space-y-1 mb-3">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{currencySymbol}{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>GST Total:</span>
                  <span>{currencySymbol}{totalGst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs font-bold border-t border-b border-slate-900 py-1">
                  <span>GRAND TOTAL:</span>
                  <span>{currencySymbol}{grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {notes && (
                <p className="text-[9px] text-slate-600 mb-2 italic">Note: {notes}</p>
              )}

              {/* Footer */}
              <div className="text-center text-[9px] text-slate-600 border-t border-dashed border-slate-800 pt-2">
                <p className="font-bold">*** STOCK RECORD VOUCHER ***</p>
                <p className="mt-0.5">{new Date().toLocaleString()}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchasePrintModal;
